<template lang="html">
  <div class="fsd-job-template__plus">
    <div
      v-if="activeheaderImage"
      class="card card--images fsd-job-template__plus-images"
    >
      <div
        v-if="isCyclingHeaderImages"
        class="fsd-job-template__plus-image-dots"
      >
        <div
          v-for="(image, idx) in headerImages"
          :key="`dot${idx}`"
          class="fsd-job-template__plus-image-dot"
          @click="onClickHeaderImageDot(idx)"
        >
          <transition name="cycle" mode="in-out">
            <div
              v-if="activeheaderImageIdx == idx"
              class="fsd-job-template__plus-image-dot--active"
            />
          </transition>
        </div>
      </div>
      <transition name="cycle" mode="in-out">
        <img
          :key="activeheaderImageIdx"
          :alt="activeheaderImageCaption"
          :src="activeheaderImage.url"
          class="fsd-job-template__plus-image"
        />
      </transition>
    </div>
    <div v-for="(card, idx) in htmlCards" :key="idx" class="card">
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="card.html"
        class="fsd-job-template__plus-content"
        v-html="card.html"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>

    <div v-if="hasMinimalContactInfo" class="card">
      <div class="fsd-job-template__plus-content">
        <h2>
          {{ contactPersonHeadline }}
        </h2>
        <div>{{ contactPerson }}</div>
        <div v-if="department" class="fsd-job-template__plus-content--detail">
          {{ department }}
        </div>
        <div v-if="contactPersonPhone">
          {{ contactPersonPhone }}
        </div>
        <div v-if="contactPersonEmail">
          <a :href="`mailto:${contactPersonEmail}`">{{ contactPersonEmail }}</a>
        </div>
      </div>
    </div>

    <div v-if="youtubeVideoUrl && consent.google !== 'UNKNOWN'" class="card">
      <youtube-video
        :youtube-video="youtubeVideoUrl"
        :meta="{ employer: employer }"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import _get from 'lodash-es/get'
import YoutubeVideo from '@/vue/components/fused/youtube_video'

const HTML_FIELDS = [
  'companyDescription',
  'responsibilities',
  'qualifications',
  'jobBenefits',
  'specialCommitments'
]

const IMAGES_MAX = 3
// in milliseconds
const IMAGES_CYCLE_SPEED = 5000

export default {
  components: { YoutubeVideo },
  props: {
    job: { type: Object, required: true }
  },
  data: () => {
    return {
      activeheaderImageIdx: null,
      headerInterval: null
    }
  },
  computed: {
    ...mapState(['consent']),
    htmlCards() {
      return HTML_FIELDS.reduce((cards, field) => {
        if (this.job && this.job.data && this.job.data[field]) {
          cards.push({ html: this.job.data[field] })
        }
        return cards
      }, [])
    },
    contactPerson() {
      const person = _get(this.job, 'data.contactPerson')
      const salutation = _get(this.job, 'data.contactPersonSalutation')

      return salutation && person ? `${salutation} ${person}` : person
    },
    contactPersonHeadline() {
      return (
        _get(this.job, 'data.contactPersonHeadline') ||
        this.$options.filters.t('client.job_card.contact.headline')
      )
    },
    contactPersonPhone() {
      return _get(this.job, 'data.contactPersonPhone')
    },
    contactPersonEmail() {
      return _get(this.job, 'data.applyEmail')
    },
    department() {
      return _get(this.job, 'data.department')
    },
    employer() {
      return _get(this.job, 'data.employer')
    },
    activeheaderImageCaption() {
      if (this.activeheaderImage && this.activeheaderImage.caption) {
        return this.activeheaderImage.caption
      }

      const caption = this.$options.filters.t(
        'client.fused.job.captions.gallery_image'
      )

      if (this.employer) {
        return `${this.employer} - ${caption}`
      } else {
        return caption
      }
    },
    hasMinimalContactInfo() {
      return !!(
        this.job &&
        this.contactPerson &&
        (this.contactPersonPhone || this.contactPersonEmail)
      )
    },
    youtubeVideoUrl() {
      if (_get(this.job, 'data.adeleMedia.videoOrigin') === 'YOUTUBE') {
        return _get(this.job, 'data.adeleMedia.video')
      } else {
        return null
      }
    },
    headerImages() {
      return _get(this.job, 'data.adeleMedia.headerImages', []).slice(
        0,
        IMAGES_MAX
      )
    },
    activeheaderImage() {
      return this.headerImages[this.activeheaderImageIdx] || null
    },
    isCyclingHeaderImages() {
      return this.headerImages.length > 1
    },
    videoOrigin() {
      return _get(this.job, 'data.adeleMedia.videoOrigin') || null
    }
  },
  mounted() {
    this.activeheaderImageIdx = 0
    if (this.isCyclingHeaderImages) {
      this.setHeaderInterval()
    }
  },
  beforeDestroy() {
    if (this.isCyclingHeaderImages) {
      clearInterval(this.headerInterval)
    }
  },
  methods: {
    setHeaderInterval() {
      this.headerInterval = setInterval(() => {
        this.activeheaderImageIdx =
          (this.activeheaderImageIdx + 1) % this.headerImages.length
      }, IMAGES_CYCLE_SPEED)
    },
    onClickHeaderImageDot(idx) {
      if (this.activeheaderImageIdx !== idx) {
        clearInterval(this.headerInterval)
        this.activeheaderImageIdx = idx
        this.setHeaderInterval()
      }
    }
  }
}
</script>

<style lang="css" scoped>
.cycle-enter-active,
.cycle-leave-active {
  transition: opacity 2s;
}
.cycle-enter,
.cycle-leave-to {
  opacity: 0;
}
</style>
